import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, switchMap, throwError, from, firstValueFrom } from "rxjs";
import { AuthService } from "shared/services/auth.service";
import { ObjectUtils } from "fakturnia-shared";

/**
 * Współdzielony Promise refresha - Promise gwarantuje że .then() zadziała
 * dla KAŻDEJ subskrypcji, nawet jeśli Promise już się zakończył.
 * To rozwiązuje problem z race condition przy wielu równoległych requestach.
 */
let refreshTokenPromise: Promise<any> | null = null;

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {

  constructor(private _authService: AuthService) { }

  // Intercept request
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    if (!request) return next.handle(request);
    if (request instanceof HttpErrorResponse) return throwError(() => request);
    // Do not proceed when logout
    if (request.url.includes('logout')) return next.handle(request);

    return next.handle(request).pipe(
      catchError((error) => {
        // Must include `TOKEN_EXPIRED` status
        const errorMessage = ObjectUtils.getNestedValue(error, "error.message");
      
        if (errorMessage === 'TOKEN_EXPIRED' && error.status === 401) {
          return this._handleTokenExpired(request, next);
        }

        throw new HttpErrorResponse(error);
      })
    );
  }
  
  private _handleTokenExpired(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    
    // Nie jesteśmy zalogowani - nie ma sensu robić refresh
    if (!this._authService.isLoggedIn()) {
      return throwError(() => new Error('Not logged in'));
    }

    // Jeśli refresh jeszcze nie trwa, rozpocznij go
    // Promise gwarantuje że WSZYSTKIE oczekujące requesty dostaną wynik
    if (!refreshTokenPromise) {
      console.log('[RefreshToken] Starting new refresh token request');
      
      refreshTokenPromise = firstValueFrom(this._authService.getRefreshToken())
        .then((response) => {
          console.log('[RefreshToken] Refresh successful');
          return response;
        })
        .catch((error) => {
          console.error('[RefreshToken] Refresh failed:', error);
          throw error;
        })
        .finally(() => {
          // Reset po krótkim opóźnieniu - daje czas wszystkim requestom na dołączenie
          // do tego samego Promise zanim zostanie zresetowany
          setTimeout(() => {
            console.log('[RefreshToken] Resetting refresh promise');
            refreshTokenPromise = null;
          }, 1000);
        });
    } else {
      console.log('[RefreshToken] Waiting for existing refresh token request');
    }

    // Czekaj na zakończenie refresha i ponów request z nowym tokenem
    return from(refreshTokenPromise).pipe(
      switchMap(() => {
        // Token został odświeżony - ponów oryginalny request z nowymi danymi
        console.log('[RefreshToken] Retrying request:', request.url);
        const updatedRequest = this._addTokenToRequest(request);
        return next.handle(updatedRequest);
      }),
      catchError((error) => {
        console.error('[RefreshToken] Request retry failed:', error);
        return throwError(() => error);
      })
    );
  }

  private _addTokenToRequest(request: HttpRequest<any>): HttpRequest<any> {
    const headers = request.headers
      .set('x-access-token', this._authService.accessToken || '')
      .set('x-session-id', this._authService.sessionId || '');
    
    return request.clone({
      withCredentials: true,
      headers: headers
    });
  }
}

